import { Component } from '@theme/component';

/**
 * Pins an element that a third-party app injects at runtime to a fixed spot in the theme's markup.
 *
 * Built for the Wishlist app (Vitals), which has no placement hook for this theme: it still
 * believes the store runs Fashionopolism and drops its icons wherever its own heuristics land —
 * the header heart as a stray row above the navigation, the product-page heart inside the first
 * image of the desktop-only media grid (so it is invisible on mobile). This doesn't reimplement
 * anything: it adopts the app's own element, so counts, clicks and every app setting keep working
 * exactly as the app intends. Only the position changes.
 *
 * Usage — an empty tag, placed where the app's element should end up:
 *
 *   <app-element-slot data-app-selector=".Vtl-WishlistHeaderWrapper" data-watch=".header-section">
 *
 * - `data-app-selector` — the app's element. Find it by rendering the page (headless browser, DOM
 *   dump); these elements never exist in the served HTML, only after the app's scripts run.
 * - `data-watch` — the nearest ancestor the app inserts into. Only that subtree is observed, and
 *   only elements found inside it are adopted, which is what stops two slots on one page (a
 *   product gallery and a quick-add gallery, say) from taking the same element from each other.
 *
 * If the app never renders its element, this does nothing and the slot stays empty; pair it with
 * an `:empty { display: none }` rule so it leaves no gap.
 *
 * @extends {Component}
 */
class AppElementSlot extends Component {
  /** @type {Element | null} */
  #element = null;

  /** @type {Element} */
  #root = document.body;

  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    if (!this.dataset.appSelector) return;

    this.#root = (this.dataset.watch && this.closest(this.dataset.watch)) || document.body;
    this.#claim();

    // Apps usually load after the theme, so the element tends to arrive later. Moving it in here is
    // itself a mutation inside the root, but #claim() no-ops once the element is already held.
    this.#observer = new MutationObserver(() => this.#claim());
    this.#observer.observe(this.#root, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * Horizon re-renders sections through `morph`, which reconciles this element's children against
   * the server-rendered markup — where the slot is empty — and drops the adopted element. It stays
   * referenced here, so it can simply be put back.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#claim();
  }

  #claim() {
    const selector = this.dataset.appSelector;
    if (!selector) return;

    const found = Array.from(this.#root.querySelectorAll(selector)).find((el) => !el.closest('app-element-slot'));

    // Nothing new from the app: re-attach the one we hold if a morph detached it, else leave it be.
    const element = found ?? (this.#element && !this.#element.isConnected ? this.#element : null);
    if (!element) return;

    // The app re-rendered by inserting a fresh element instead of updating the old one — keep
    // exactly one, the newest, since that is the one the app is now driving.
    if (this.#element && this.#element !== element) this.#element.remove();

    this.#element = element;
    this.append(element);
  }
}

if (!customElements.get('app-element-slot')) customElements.define('app-element-slot', AppElementSlot);
